/**
 * Export Excel-style Table to CSV
 */

import { COLUMN_MODES, GALLONS_PER_ACRE_FOOT } from "../constants";
import { downloadCSV } from "./downloadCSV";

/**
 * Export excel-style grouped data to CSV
 * @param {Array} sortedGroups - Sorted groups with wells
 * @param {Object} grandTotals - Grand totals
 * @param {string} columnMode - Column display mode
 */
export const exportExcelTable = (sortedGroups, grandTotals, columnMode) => {
  const headers =
    columnMode === COLUMN_MODES.compact
      ? [
          "Row Type",
          "File No",
          "Agg System",
          "State #",
          "Well Name",
          "Year",
          "Aquifer",
          "Hist. Permit (ac-ft)",
          "Oper. Permit (ac-ft)",
          "Total Permit (ac-ft)",
          "YTD (gal)",
          "Remaining (gal)",
          "YTD (ac-ft)",
          "Remaining (ac-ft)",
          "% Permit",
        ]
      : [
          "Row Type",
          "File No",
          "Agg System",
          "State #",
          "Well Name",
          "Year",
          "Aquifer",
          "Hist. Permit (ac-ft)",
          "Oper. Permit (ac-ft)",
          "Total Permit (ac-ft)",
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
          "YTD (gal)",
          "Remaining (gal)",
          "YTD (ac-ft)",
          "Remaining (ac-ft)",
          "% Permit",
        ];

  const csvRows = [headers];

  // Export each group with its subtotal and wells
  sortedGroups.forEach((group) => {
    const groupRemainingGal =
      group.totals.h_permit_value_gal +
      group.totals.o_permit_value_gal -
      group.totals.ytd_gal;
    const groupRemainingAf = groupRemainingGal / GALLONS_PER_ACRE_FOOT;

    // Group subtotal row (now includes year since groups are per agg system + year)
    const groupRow = [
      "Subtotal",
      "",
      group.agg_system_name || "",
      "",
      "",
      group.report_year || "",
      "",
      group.totals.h_permit_af,
      group.totals.o_permit_af,
      group.totals.total_permit_af,
    ];

    if (columnMode === COLUMN_MODES.full) {
      groupRow.push(
        group.totals.jan,
        group.totals.feb,
        group.totals.mar,
        group.totals.apr,
        group.totals.may,
        group.totals.jun,
        group.totals.jul,
        group.totals.aug,
        group.totals.sep,
        group.totals.oct,
        group.totals.nov,
        group.totals.dec
      );
    }

    groupRow.push(
      group.totals.ytd_gal,
      groupRemainingGal,
      group.totals.ytd_af,
      groupRemainingAf,
      group.pct_permit
    );

    csvRows.push(groupRow);

    // Individual wells in this group
    group.wells.forEach((well) => {
      const wellRemainingGal =
        (well.h_permit_value_gal || 0) +
        (well.o_permit_value_gal || 0) -
        (well.ytd_gal || 0);
      const wellRemainingAf = wellRemainingGal / GALLONS_PER_ACRE_FOOT;
      const wellPctPermit =
        (well.h_permit_value_gal || 0) + (well.o_permit_value_gal || 0) > 0
          ? (well.ytd_gal || 0) /
            ((well.h_permit_value_gal || 0) + (well.o_permit_value_gal || 0))
          : 0;

      const wellRow = [
        "Well",
        well.cuwcd_well_number || "",
        well.agg_system_name || "",
        well.state_well_number || "",
        well.well_name || "",
        well.report_year,
        well.aquifer_name || "",
        well.h_permit_af,
        well.o_permit_af,
        well.total_permit_af,
      ];

      if (columnMode === COLUMN_MODES.full) {
        wellRow.push(
          well.jan,
          well.feb,
          well.mar,
          well.apr,
          well.may,
          well.jun,
          well.jul,
          well.aug,
          well.sep,
          well.oct,
          well.nov,
          well.dec
        );
      }

      wellRow.push(
        well.ytd_gal,
        wellRemainingGal,
        well.ytd_af,
        wellRemainingAf,
        wellPctPermit
      );

      csvRows.push(wellRow);
    });
  });

  // Grand total row
  const grandRemainingGal =
    grandTotals.h_permit_value_gal +
    grandTotals.o_permit_value_gal -
    grandTotals.ytd_gal;
  const grandRemainingAf = grandRemainingGal / GALLONS_PER_ACRE_FOOT;

  const grandTotalRow = [
    "Grand Total",
    "",
    "",
    "",
    "",
    "",
    "",
    grandTotals.h_permit_af,
    grandTotals.o_permit_af,
    grandTotals.total_permit_af,
  ];

  if (columnMode === COLUMN_MODES.full) {
    grandTotalRow.push(
      grandTotals.jan,
      grandTotals.feb,
      grandTotals.mar,
      grandTotals.apr,
      grandTotals.may,
      grandTotals.jun,
      grandTotals.jul,
      grandTotals.aug,
      grandTotals.sep,
      grandTotals.oct,
      grandTotals.nov,
      grandTotals.dec
    );
  }

  grandTotalRow.push(
    grandTotals.ytd_gal,
    grandRemainingGal,
    grandTotals.ytd_af,
    grandRemainingAf,
    grandTotals.pct_permit
  );

  csvRows.push(grandTotalRow);

  downloadCSV(csvRows, "agg-system-grouped-data.csv");
};
